import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {Link} from "react-router-dom";
import styled, {keyframes} from "styled-components";
import {
    getGuestConversations,
    getGuestMessages,
    guestErrorCode,
    guestErrorMessage,
    guestSession,
    redeemGuestLink,
    sendGuestReply,
} from "api/guest.api";
import {track} from "analytics";
import {
    Alert,
    AlertIcon,
    Avatar,
    ChatIcon,
    ChevronLeftIcon,
    ClockIcon,
    IconBadge,
    LockIcon,
    Muted,
    PillButton,
    PillButtonOutline,
    PillLink,
    SendIcon,
    Spinner,
    tokens,
} from "ui/blocks/guestMessaging/GuestUI";

const POLL_MS = 15000;
const MESSAGE_MAX = 2000;

const newKey = () =>
    window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const readLinkToken = () => {
    const match = window.location.hash.match(/token=([A-Za-z0-9_-]+)/);
    return match ? match[1] : "";
};

const sameDay = (a, b) => a.toDateString() === b.toDateString();

const dayLabel = (date) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (sameDay(date, today)) return "Today";
    if (sameDay(date, yesterday)) return "Yesterday";
    return date.toLocaleDateString([], {month: "short", day: "numeric", year: date.getFullYear() === today.getFullYear() ? undefined : "numeric"});
};

const timeLabel = (date) => date.toLocaleTimeString([], {hour: "numeric", minute: "2-digit"});

const profilePath = (instructorUserId) => (instructorUserId ? `/user/class-detail/${instructorUserId}` : "/");

/**
 * Guest inbox, reached from the sign-in link in Teache emails. Guests read
 * and reply through the backend only; they never get account access.
 */
export const GuestMessages = () => {
    const [linkToken] = useState(readLinkToken);
    const [session, setSession] = useState(() => (readLinkToken() ? null : guestSession.get()));
    const [status, setStatus] = useState(session ? "loading" : linkToken ? "confirm" : "expired");
    const [error, setError] = useState("");
    const [conversations, setConversations] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [showList, setShowList] = useState(false); // mobile: list vs thread
    const [thread, setThread] = useState(null);
    const [pending, setPending] = useState([]); // replies not yet confirmed

    const expire = useCallback(() => {
        guestSession.clear();
        setSession(null);
        setStatus("expired");
    }, []);

    const handleApiError = useCallback((err) => {
        if (err?.response?.status === 401) {
            expire();
            return true;
        }
        return false;
    }, [expire]);

    // Sign in with the link only when the person clicks the button, so email
    // security scanners that open links can't use up the single-use link.
    const redeem = async () => {
        setStatus("redeeming");
        setError("");
        try {
            const s = await redeemGuestLink(linkToken);
            guestSession.set(s);
            window.history.replaceState(null, "", window.location.pathname);
            setSession(s);
            setStatus("loading");
        } catch (err) {
            if (guestErrorCode(err)) {
                setError(guestErrorMessage(err));
                setStatus("expired");
            } else {
                setError("We couldn't connect. Check your connection and try again.");
                setStatus("confirm");
            }
        }
    };

    const loadConversations = useCallback(async () => {
        if (!session) return;
        setStatus((s) => (s === "ready" ? s : "loading"));
        try {
            const list = await getGuestConversations(session.token);
            setConversations(list);
            setActiveId((current) => current || list[0]?.id || null);
            setShowList(list.length > 1);
            setStatus("ready");
        } catch (err) {
            if (!handleApiError(err)) {
                setError(guestErrorMessage(err, "We couldn't load your messages."));
                setStatus("error");
            }
        }
    }, [session, handleApiError]);

    const refreshConversations = useCallback(async () => {
        if (!session) return;
        try {
            setConversations(await getGuestConversations(session.token));
        } catch (err) {
            handleApiError(err);
        }
    }, [session, handleApiError]);

    const loadThread = useCallback(async () => {
        if (!session || !activeId) return;
        try {
            const data = await getGuestMessages(session.token, activeId);
            setThread((prev) => (prev?.conversation?.id === activeId || !prev ? data : prev));
        } catch (err) {
            handleApiError(err);
        }
    }, [session, activeId, handleApiError]);

    useEffect(() => {
        if (session) {
            track("guest_inbox_viewed");
            loadConversations();
        }
    }, [session, loadConversations]);

    useEffect(() => {
        if (!activeId) return undefined;
        setThread(null);
        loadThread();
        const id = setInterval(() => {
            if (document.visibilityState === "visible") {
                loadThread();
                refreshConversations();
            }
        }, POLL_MS);
        return () => clearInterval(id);
    }, [activeId, loadThread, refreshConversations]);

    const sendReply = async (item) => {
        setPending((list) => list.map((p) => (p.key === item.key ? {...p, status: "sending"} : p)));
        try {
            await sendGuestReply(session.token, item.conversationId, {message: item.text, idempotency_key: item.key});
            await loadThread();
            setPending((list) => list.filter((p) => p.key !== item.key));
            refreshConversations();
        } catch (err) {
            if (handleApiError(err)) return;
            track("message_send_failed", {guest: true, reply: true, error: guestErrorCode(err) || "network"});
            setPending((list) =>
                list.map((p) => (p.key === item.key ? {...p, status: "failed", error: guestErrorMessage(err, "Not sent")} : p)));
        }
    };

    const submitReply = (text) => {
        track("message_submitted", {guest: true, reply: true, length: text.length});
        // The idempotency key stays with the message, so "Retry" can never
        // deliver it twice.
        const item = {key: newKey(), text, conversationId: activeId, status: "sending", at: new Date()};
        setPending((list) => [...list, item]);
        sendReply(item);
    };

    if (status === "confirm" || status === "redeeming") {
        return (
            <Page>
                <StatusCard>
                    <IconBadge><ChatIcon size={28}/></IconBadge>
                    <StatusTitle>Your Teache messages</StatusTitle>
                    <StatusBody>Open your conversation to read replies and write back.</StatusBody>
                    {error && <Alert role="alert"><AlertIcon/><span>{error}</span></Alert>}
                    <PillButton type="button" $block onClick={redeem} disabled={status === "redeeming"}>
                        {status === "redeeming" ? <><Spinner $light $size={16}/> Opening…</> : "View conversation"}
                    </PillButton>
                    <Muted><LockIcon size={14}/><span>This secure link works once. No password needed.</span></Muted>
                </StatusCard>
            </Page>
        );
    }

    if (status === "expired") {
        return (
            <Page>
                <StatusCard>
                    <IconBadge $tone="neutral"><ClockIcon size={28}/></IconBadge>
                    <StatusTitle>This link has expired</StatusTitle>
                    <StatusBody>
                        For your security, sign-in links work once and expire after 7 days. Use the link in your
                        latest email from Teache. You'll get a new one each time an instructor replies.
                    </StatusBody>
                    <PillLink as={Link} to="/" style={{minHeight: 48, padding: "0 28px", fontSize: 15}}>Browse instructors</PillLink>
                </StatusCard>
            </Page>
        );
    }

    if (status === "error") {
        return (
            <Page>
                <StatusCard>
                    <IconBadge $tone="neutral"><AlertIcon size={28}/></IconBadge>
                    <StatusTitle>Something went wrong</StatusTitle>
                    <StatusBody>{error}</StatusBody>
                    <PillButton type="button" $block onClick={loadConversations}>Try again</PillButton>
                </StatusCard>
            </Page>
        );
    }

    if (status === "loading") {
        return (
            <Page>
                <Layout>
                    <ThreadCard aria-busy="true" aria-label="Loading your conversation">
                        <ThreadHeader>
                            <SkeletonCircle/>
                            <SkeletonLine style={{width: 140}}/>
                        </ThreadHeader>
                        <MessagesArea>
                            <SkeletonBubble style={{alignSelf: "flex-end", width: "58%"}}/>
                            <SkeletonBubble style={{width: "46%"}}/>
                            <SkeletonBubble style={{alignSelf: "flex-end", width: "36%"}}/>
                        </MessagesArea>
                    </ThreadCard>
                </Layout>
            </Page>
        );
    }

    // ready
    const active = conversations.find((c) => c.id === activeId) || thread?.conversation;
    const multi = conversations.length > 1;

    return (
        <Page>
            <Layout $multi={multi}>
                {multi && (
                    <ListCard $hideOnMobile={!showList} aria-label="Conversations">
                        <ListTitle>Messages</ListTitle>
                        {conversations.map((c) => (
                            <ListItem
                                key={c.id}
                                type="button"
                                $active={c.id === activeId}
                                onClick={() => {
                                    setActiveId(c.id);
                                    setShowList(false);
                                }}
                            >
                                <Avatar src={c.instructor_picture} name={c.instructor_name} size={44}/>
                                <ListText>
                                    <strong>{c.instructor_name}</strong>
                                    <span>{c.last_message}</span>
                                </ListText>
                                {c.unread > 0 && c.id !== activeId && <Badge>{c.unread}</Badge>}
                            </ListItem>
                        ))}
                    </ListCard>
                )}

                {conversations.length === 0 ? (
                    <StatusCard>
                        <IconBadge $tone="neutral"><ChatIcon size={28}/></IconBadge>
                        <StatusTitle>No conversations yet</StatusTitle>
                        <StatusBody>Messages you send to instructors will appear here.</StatusBody>
                        <PillLink as={Link} to="/" style={{minHeight: 48, padding: "0 28px", fontSize: 15}}>Browse instructors</PillLink>
                    </StatusCard>
                ) : (
                    <ThreadCard $hideOnMobile={multi && showList}>
                        <ThreadHeader>
                            {multi && (
                                <BackButton type="button" onClick={() => setShowList(true)} aria-label="All conversations">
                                    <ChevronLeftIcon size={22}/>
                                </BackButton>
                            )}
                            <Avatar src={active?.instructor_picture} name={active?.instructor_name || ""} size={44}/>
                            <HeaderText>
                                <h1>{active?.instructor_name || "Conversation"}</h1>
                                <span>Instructor</span>
                            </HeaderText>
                            <PillLink as={Link} to={profilePath(active?.instructor_user_id)}>View profile</PillLink>
                        </ThreadHeader>

                        <Thread
                            messages={thread?.messages}
                            pending={pending.filter((p) => p.conversationId === activeId)}
                            loading={!thread}
                            onRetry={sendReply}
                        />

                        <Composer onSend={submitReply}/>
                    </ThreadCard>
                )}
            </Layout>
            <Footnote>
                <LockIcon size={14}/>
                <span>You're messaging as a guest. Your email is never shared with the instructor. Keep your conversation on Teache.</span>
            </Footnote>
        </Page>
    );
};

const Thread = ({messages, pending, loading, onRetry}) => {
    const areaRef = useRef(null);
    const count = (messages?.length || 0) + pending.length;

    useLayoutEffect(() => {
        const el = areaRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [count]);

    if (loading) {
        return (
            <MessagesArea>
                <Centered><Spinner $size={22}/></Centered>
            </MessagesArea>
        );
    }

    const items = [];
    let lastDay = null;
    (messages || []).forEach((m) => {
        const at = m.timestamp ? new Date(m.timestamp) : null;
        if (at && (!lastDay || !sameDay(at, lastDay))) {
            items.push(<DaySeparator key={`d-${m.id}`}><span>{dayLabel(at)}</span></DaySeparator>);
            lastDay = at;
        }
        items.push(
            <BubbleRow key={m.id} $mine={m.from_me}>
                <Bubble $mine={m.from_me}>{m.text}</Bubble>
                {at && <Meta>{timeLabel(at)}</Meta>}
            </BubbleRow>
        );
    });
    pending.forEach((p) => {
        items.push(
            <BubbleRow key={p.key} $mine>
                <Bubble $mine $pending={p.status === "sending"} $failed={p.status === "failed"}>{p.text}</Bubble>
                {p.status === "sending" ? (
                    <Meta>Sending…</Meta>
                ) : (
                    <Meta $error>
                        Not sent.{" "}
                        <RetryButton type="button" onClick={() => onRetry(p)}>Retry</RetryButton>
                    </Meta>
                )}
            </BubbleRow>
        );
    });

    return (
        <MessagesArea ref={areaRef} role="log" aria-live="polite">
            {items.length ? items : <Centered><Muted>No messages yet.</Muted></Centered>}
        </MessagesArea>
    );
};

const Composer = ({onSend}) => {
    const [text, setText] = useState("");
    const [error, setError] = useState("");
    const ref = useRef(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [text]);

    const send = () => {
        const value = text.trim();
        if (!value) return;
        if (value.length > MESSAGE_MAX) {
            setError(`Keep it under ${MESSAGE_MAX} characters.`);
            return;
        }
        onSend(value);
        setText("");
        setError("");
        ref.current?.focus();
    };

    return (
        <ComposerWrap
            onSubmit={(e) => {
                e.preventDefault();
                send();
            }}
        >
            {error && <Alert role="alert"><AlertIcon/><span>{error}</span></Alert>}
            <ComposerRow>
                <label htmlFor="guest-reply" className="sr-only">Write a reply</label>
                <ComposerInput
                    id="guest-reply"
                    ref={ref}
                    rows={1}
                    placeholder="Write a reply…"
                    value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        setError("");
                    }}
                    onKeyDown={(e) => {
                        // Enter sends on devices with a physical keyboard; Shift+Enter adds a line.
                        if (e.key === "Enter" && !e.shiftKey && window.matchMedia?.("(pointer: fine)").matches) {
                            e.preventDefault();
                            send();
                        }
                    }}
                />
                <SendButton type="submit" disabled={!text.trim()} aria-label="Send">
                    <SendIcon size={18}/>
                </SendButton>
            </ComposerRow>
        </ComposerWrap>
    );
};

// ---------------------------------------------------------------------------
// styles

const Page = styled.main`
    box-sizing: border-box;
    min-height: calc(100dvh - 64px);
    background: ${tokens.warm};
    padding: 32px 16px 48px;
    font-family: var(--font-primary);
    color: ${tokens.text};

    @media (max-width: 767px) {
        padding: 12px 12px 32px;
    }

    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
    }
`;

const StatusCard = styled.section`
    box-sizing: border-box;
    width: 100%;
    max-width: 440px;
    margin: 48px auto;
    padding: 40px 32px 32px;
    background: #fff;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 14px;
    box-shadow: 0 1px 2px rgba(29, 29, 31, 0.04), 0 8px 24px rgba(29, 29, 31, 0.06);

    > button, > a {
        margin-top: 10px;
    }

    @media (max-width: 767px) {
        margin: 24px auto;
        padding: 32px 20px 24px;
    }
`;

const StatusTitle = styled.h1`
    margin: 6px 0 0;
    font-size: 24px;
    font-weight: 600;
    color: ${tokens.ink};
`;

const StatusBody = styled.p`
    margin: 0;
    text-wrap: balance;
    font-size: 15px;
    line-height: 1.55;
    color: ${tokens.muted};
`;

const Layout = styled.div`
    max-width: ${({$multi}) => ($multi ? "1040px" : "760px")};
    margin: 0 auto;
    display: grid;
    gap: 16px;
    grid-template-columns: minmax(0, 1fr);

    @media (min-width: 900px) {
        grid-template-columns: ${({$multi}) => ($multi ? "300px minmax(0, 1fr)" : "minmax(0, 1fr)")};
    }
`;

const cardBase = `
    box-sizing: border-box;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 1px 2px rgba(29, 29, 31, 0.04), 0 8px 24px rgba(29, 29, 31, 0.06);
`;

const ListCard = styled.nav`
    ${cardBase};
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-self: start;
    min-width: 0;

    @media (max-width: 899px) {
        display: ${({$hideOnMobile}) => ($hideOnMobile ? "none" : "flex")};
    }
`;

const ListTitle = styled.h2`
    margin: 8px 8px 8px;
    font-size: 18px;
    font-weight: 600;
    color: ${tokens.ink};
`;

const ListItem = styled.button`
    all: unset;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px;
    border-radius: 12px;
    cursor: pointer;
    background: ${({$active}) => ($active ? tokens.accentSoft : "transparent")};

    &:hover {
        background: ${({$active}) => ($active ? tokens.accentSoft : tokens.warm)};
    }

    &:focus-visible {
        outline: 2px solid ${tokens.accent};
    }
`;

const ListText = styled.span`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;

    strong {
        font-size: 15px;
        font-weight: 600;
        color: ${tokens.ink};
    }

    span {
        font-size: 13px;
        color: ${tokens.muted};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const Badge = styled.span`
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    box-sizing: border-box;
    border-radius: 10px;
    background: ${tokens.accent};
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const ThreadCard = styled.section`
    ${cardBase};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: min(720px, calc(100dvh - 200px));
    min-height: 460px;

    @media (max-width: 767px) {
        height: calc(100dvh - 150px);
        min-height: 420px;
    }

    @media (max-width: 899px) {
        display: ${({$hideOnMobile}) => ($hideOnMobile ? "none" : "flex")};
    }
`;

const ThreadHeader = styled.header`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid ${tokens.borderSoft};

    @media (min-width: 768px) {
        padding: 16px 20px;
    }
`;

const HeaderText = styled.div`
    flex: 1;
    min-width: 0;

    h1 {
        margin: 0;
        font-size: 17px;
        font-weight: 600;
        color: ${tokens.ink};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    span {
        font-size: 13px;
        color: ${tokens.muted};
    }
`;

const BackButton = styled.button`
    all: unset;
    display: none;
    width: 36px;
    height: 36px;
    margin-left: -8px;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    color: ${tokens.ink};
    cursor: pointer;

    @media (max-width: 899px) {
        display: inline-flex;
    }
`;

const MessagesArea = styled.div`
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 20px 16px;
    background: rgba(167, 166, 206, 0.1);

    @media (min-width: 768px) {
        padding: 24px;
    }
`;

const Centered = styled.div`
    margin: auto;
`;

const DaySeparator = styled.div`
    display: flex;
    justify-content: center;
    margin: 10px 0 6px;

    span {
        font-size: 12px;
        font-weight: 500;
        color: ${tokens.muted};
        background: #fff;
        padding: 4px 12px;
        border-radius: 100px;
    }
`;

const BubbleRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: ${({$mine}) => ($mine ? "flex-end" : "flex-start")};
    gap: 3px;
    margin-bottom: 6px;
`;

const Bubble = styled.div`
    max-width: min(78%, 520px);
    padding: 10px 14px;
    border-radius: ${({$mine}) => ($mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px")};
    background: ${({$mine}) => ($mine ? tokens.accent : "#fff")};
    color: ${({$mine}) => ($mine ? "#fff" : tokens.text)};
    font-size: 15px;
    line-height: 1.45;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    opacity: ${({$pending}) => ($pending ? 0.6 : 1)};
    ${({$failed}) => $failed && `background: ${tokens.errorSoft}; color: ${tokens.text};`}
`;

const Meta = styled.span`
    font-size: 11px;
    color: ${({$error}) => ($error ? tokens.error : tokens.muted)};
    padding: 0 4px;
`;

const RetryButton = styled.button`
    all: unset;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
`;

const ComposerWrap = styled.form`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 12px;
    border-top: 1px solid ${tokens.borderSoft};
    background: #fff;

    @media (min-width: 768px) {
        padding: 14px 20px 16px;
    }
`;

const ComposerRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 10px;
`;

const ComposerInput = styled.textarea`
    flex: 1;
    box-sizing: border-box;
    min-height: 48px;
    max-height: 160px;
    padding: 12px 16px;
    border-radius: 24px;
    border: 1px solid ${tokens.border};
    font-family: var(--font-primary);
    font-size: 16px;
    line-height: 1.4;
    color: ${tokens.text};
    resize: none;

    &::placeholder {
        color: ${tokens.placeholder};
    }

    &:focus {
        outline: none;
        border-color: ${tokens.accent};
        box-shadow: 0 0 0 3px ${tokens.accentSoft};
    }
`;

const SendButton = styled.button`
    all: unset;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${tokens.ink};
    color: #fff;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${tokens.accent};
        outline-offset: 3px;
    }
`;

const Footnote = styled(Muted)`
    max-width: 760px;
    margin: 16px auto 0;
    padding: 0 4px;
`;

const shimmer = keyframes`
    0% { opacity: 0.55; }
    50% { opacity: 1; }
    100% { opacity: 0.55; }
`;

const SkeletonCircle = styled.span`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: ${tokens.borderSoft};
    animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const SkeletonLine = styled.span`
    height: 14px;
    border-radius: 7px;
    background: ${tokens.borderSoft};
    animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const SkeletonBubble = styled.span`
    height: 44px;
    border-radius: 16px;
    background: #fff;
    animation: ${shimmer} 1.4s ease-in-out infinite;
`;
